import { FabricSceneQueryService } from '@/infrastructure/engine2d/scene/FabricSceneQueryService';
import { FabricSceneWriteService } from '@/infrastructure/engine2d/scene/FabricSceneWriteService';
import { LOCKED_FOREGROUND_GROUP_NAME } from '@/infrastructure/engine2d/fabricGroupsName';
import { keepLockedBackgroundObjectGroupToBack } from '@/infrastructure/engine2d/helpers/FabricGroupHelper';
import type { Canvas } from 'fabric';
import type { FabricPortEventsService } from '@/infrastructure/engine2d/events/FabricPortEventsService';
import type { FabricScene } from '@/infrastructure/engine2d/FabricScene';
import type { I2DEngine } from '@/application/ports/I2DEngine';
import type { TAddViewOptions } from '@/application/ports/TAddViewOptions';
import type { TImagePropertyPatch } from '@/domain/image/TImagePropertyPatch';
import type { TSelectionRange } from '@/domain/textbox/TSelectionRange';
import type { TTextLayoutPatch } from '@/domain/textbox/TTextLayoutPatch';
import type { TTextLayoutResult } from '@/domain/textbox/TTextLayoutResult';
import type { TTextStylePatch } from '@/domain/textbox/TTextStylePatch';
import type { TTextStyleResult } from '@/domain/textbox/TTextStyleResult';
import type { TViewTransform } from '@/application/ports/TViewTransform';
import type { AField } from '@/domain/field/AField';

type TFabricEngine2DParams = {
    events: FabricPortEventsService;
    scene: FabricScene;
};

// Fabric adapter for I2DEngine. Thin façade over three sub-services:
// events (shared port bus — also consumed by FabricViewportEngine2D),
// queries (reads), writes (mutations). Owns entity CRUD + patches +
// z-order enforcement + domain events + selection reads. Viewport
// concerns live in FabricViewportEngine2D; chrome in
// FabricPrintZoneEngine2D.
class FabricEngine2D implements I2DEngine {
    private readonly _canvas: Canvas;
    private readonly _events: FabricPortEventsService;
    private readonly _queries: FabricSceneQueryService;
    private readonly _writes: FabricSceneWriteService;

    constructor(params: TFabricEngine2DParams) {
        this._canvas = params.scene.fabricCanvas;
        this._events = params.events;
        this._queries = new FabricSceneQueryService({ canvas: this._canvas });
        this._writes = new FabricSceneWriteService({ canvas: this._canvas, scene: params.scene });
    }

    addView(entity: AField, options?: TAddViewOptions): Promise<void> {
        return this._writes.addView(entity, options);
    }

    applyImageProperty(entityId: string, patch: TImagePropertyPatch): Promise<void> {
        return this._writes.applyImageProperty(entityId, patch);
    }

    applyTextLayout(entityId: string, patch: TTextLayoutPatch): TTextLayoutResult | null {
        return this._writes.applyTextLayout(entityId, patch);
    }

    applyTextStyle(entityId: string, patch: TTextStylePatch, range?: TSelectionRange): TTextStyleResult | null {
        return this._writes.applyTextStyle(entityId, patch, range);
    }

    dispatchZoomChanged(zoom: number): void {
        this._events.dispatchZoomChanged(zoom);
    }

    dispose(): void {
        // The shared FabricPortEventsService is disposed by the
        // composition root — not owned by this adapter.
    }

    getActiveEntityId(): string | undefined {
        return this._queries.getActiveEntityId();
    }

    getActiveObjectAnchors():
        | { mb: { x: number; y: number }; ml: { x: number; y: number }; mr: { x: number; y: number }; mt: { x: number; y: number } }
        | undefined {
        return this._queries.getActiveObjectAnchors();
    }

    initLockedBackgroundGroup(): void {
        this._writes.initLockedBackgroundGroup();
    }

    isEditingActiveObject(): boolean {
        return this._queries.isEditingActiveObject();
    }

    keepAlwaysOnTopFieldsUpFront(): void {
        const topFields = this._canvas.getObjects('image').filter((img) => (img as { alwaysOnTop?: boolean }).alwaysOnTop);
        if (topFields.length === 0) {
            return;
        }
        topFields.forEach((img) => this._canvas.bringObjectToFront(img));
        this._canvas.renderAll();
    }

    keepLockedBackgroundGroupToBack(): void {
        keepLockedBackgroundObjectGroupToBack(this._canvas, LOCKED_FOREGROUND_GROUP_NAME);
    }

    onSelectionChanged(callback: (entityId: string | undefined) => void): () => void {
        return this._events.onSelectionChanged(callback);
    }

    onTextChanged(callback: (payload: { id: string; text: string }) => void): () => void {
        return this._events.onTextChanged(callback);
    }

    onTextSelectionChanged(callback: (range: { end: number; start: number } | undefined) => void): () => void {
        return this._events.onTextSelectionChanged(callback);
    }

    onViewTransformed(callback: (transform: TViewTransform) => void): () => void {
        return this._events.onViewTransformed(callback);
    }

    refreshTextboxesAfterHydration(entities: AField[]): void {
        this._writes.refreshTextboxesAfterHydration(entities);
    }

    removeView(entityId: string): void {
        this._writes.removeView(entityId);
    }

    renderNow(): void {
        this._writes.renderNow();
    }

    setSelectedEntity(entityId: string | undefined): void {
        this._writes.setActiveEntity(entityId);
    }

    setAutoRender(enabled: boolean): void {
        this._writes.setAutoRender(enabled);
    }

    setZOrder(entityIds: string[]): void {
        this._writes.setZOrder(entityIds);
    }

    updateView(entity: AField): void {
        this._writes.updateView(entity);
    }
}

export { FabricEngine2D };
